import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { carTestLabels } from "@/i18n";

const VIBRATION_LOCATIONS = [
  "Styrning/Ratt",
  "Hjul/Däck",
  "Fjädring",
  "Motor",
  "Annat",
] as const;

interface VibrationsSectionProps {
  vibrations: { problem?: string; location?: string; other?: string } | null;
  onVibrationsChange: (vibrations: {
    problem?: string;
    location?: string;
    other?: string;
  }) => void;
  onNext: () => void;
}

export const VibrationsSection = ({
  vibrations,
  onVibrationsChange,
  onNext,
}: VibrationsSectionProps) => {
  const [otherText, setOtherText] = useState(vibrations?.other || "");
  const { language } = useLanguage();
  const ct = carTestLabels(language);
  const form = ct.form ?? {};
  const v = ct.pdf?.values ?? ({} as Record<string, string>);
  const opt =
    (v as { optionValues?: Record<string, string> }).optionValues ?? {};

  const handleResultChange = (result: string) => {
    if (result === "no") {
      onVibrationsChange({ problem: "no" });
    } else {
      onVibrationsChange({
        problem: "yes",
        location: undefined,
        other: undefined,
      });
    }
  };

  const handleLocationChange = (location: string) => {
    if (location === "Annat") {
      onVibrationsChange({ ...vibrations, location, other: otherText });
    } else {
      onVibrationsChange({ ...vibrations, location, other: undefined });
    }
  };

  const handleOtherTextChange = (text: string) => {
    setOtherText(text);
    if (vibrations?.location === "Annat") {
      onVibrationsChange({ ...vibrations, other: text });
    }
  };

  const showNextButton =
    vibrations?.problem === "yes" &&
    vibrations?.location === "Annat" &&
    otherText.trim();

  return (
    <div className="space-y-4">
      <div>
        <h4 className="font-medium text-sm mb-3">{form.questionVibrations}</h4>
        <div className="grid grid-cols-2 gap-1.5 mb-4">
          <Button
            onClick={() => handleResultChange("yes")}
            variant="formOption"
            className={`${
              vibrations?.problem === "yes"
                ? "border-accent border-2"
                : "border-gray-400"
            }`}
          >
            {v.yes}
          </Button>
          <Button
            onClick={() => handleResultChange("no")}
            variant="formOption"
            className={`${
              vibrations?.problem === "no"
                ? "border-accent border-2"
                : "border-gray-400"
            }`}
          >
            {v.no}
          </Button>
        </div>

        {vibrations?.problem === "yes" && (
          <div className="space-y-3">
            <h5 className="font-medium text-sm">
              {form.questionVibrationLocation}
            </h5>
            <div className="grid grid-cols-2 gap-1.5">
              {VIBRATION_LOCATIONS.map((location) => (
                <Button
                  key={location}
                  onClick={() => handleLocationChange(location)}
                  variant="formOption"
                  className={`${
                    vibrations?.location === location
                      ? "border-accent border-4"
                      : "border-gray-400"
                  }`}
                >
                  {opt[location] ?? location}
                </Button>
              ))}
            </div>

            {vibrations?.location === "Annat" && (
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder={form.placeholderDescribeVibration}
                  value={otherText}
                  onChange={(e) => handleOtherTextChange(e.target.value)}
                  className="w-full"
                />
              </div>
            )}
          </div>
        )}

        {showNextButton && (
          <Button className="w-full mt-4" onClick={onNext}>
            {form.next}
          </Button>
        )}
      </div>
    </div>
  );
};
